import { buildCguBodyHtml } from '@domain/legal/CguContent';
import { CGU_PERIOD, CGU_TITLE, CGU_VERSION, cguScrollRatio, hasReadCguEnough } from '@domain/legal/CguVersion';
import { cguSubtitle, escapeHtml } from '@domain/legal/IssuerInfo';
import { getIssuerInfo } from '@infra/legal/IssuerInfoService';
import './cgu-modal.css';

export type CguModalDecision = 'accepted' | 'declined';

/**
 * Modal CGU non fermable (ni Échap, ni clic extérieur, ni ×).
 * Accepter n’est actif qu’après lecture ≥ 95 % du texte.
 */
export function presentCguModal(host: HTMLElement): Promise<CguModalDecision> {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'cgu-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'cgu-title');
    renderModal(overlay, resolve);
    host.appendChild(overlay);

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener('keydown', onKey, true);
    overlay.addEventListener('cgu:done', () => {
      window.removeEventListener('keydown', onKey, true);
    });
  });
}

function renderModal(overlay: HTMLElement, resolve: (decision: CguModalDecision) => void): void {
  overlay.innerHTML = `
    <div class="cgu-dialog">
      <header class="cgu-header">
        <h2 id="cgu-title">${CGU_TITLE}</h2>
        <p>${escapeHtml(cguSubtitle(getIssuerInfo(), CGU_VERSION, CGU_PERIOD))}</p>
      </header>
      <p class="cgu-alert">L'accès à la plateforme est subordonné à l'acceptation intégrale des présentes CGU.</p>
      <div class="cgu-progress-track" aria-hidden="true"><div class="cgu-progress-bar" id="cgu-progress"></div></div>
      <div class="cgu-body" id="cgu-body" tabindex="0">${buildCguBodyHtml(getIssuerInfo())}</div>
      <footer class="cgu-footer">
        <p class="cgu-hint" id="cgu-hint">Faites défiler jusqu'en bas pour activer les boutons</p>
        <div class="cgu-actions">
          <button type="button" class="cgu-decline" id="cgu-decline">Décliner</button>
          <button type="button" class="cgu-accept" id="cgu-accept" disabled>Accepter et accéder à l'ERP →</button>
        </div>
      </footer>
    </div>
  `;

  const body = overlay.querySelector<HTMLElement>('#cgu-body');
  const bar = overlay.querySelector<HTMLElement>('#cgu-progress');
  const accept = overlay.querySelector<HTMLButtonElement>('#cgu-accept');
  const decline = overlay.querySelector<HTMLButtonElement>('#cgu-decline');
  const hint = overlay.querySelector<HTMLElement>('#cgu-hint');
  if (!body || !bar || !accept || !decline) return;

  const syncScroll = () => {
    const ratio = cguScrollRatio(body.scrollTop, body.scrollHeight, body.clientHeight);
    bar.style.width = `${Math.round(ratio * 100)}%`;
    const ready = hasReadCguEnough(ratio);
    accept.disabled = !ready;
    if (hint) {
      hint.textContent = ready
        ? 'Vous pouvez accepter les CGU pour accéder à Nexxus ERP.'
        : "Faites défiler jusqu'en bas pour activer les boutons";
    }
  };
  body.addEventListener('scroll', syncScroll, { passive: true });
  requestAnimationFrame(syncScroll);

  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) event.stopPropagation();
  });

  decline.addEventListener('click', () => {
    renderDecline(overlay, resolve);
  });

  accept.addEventListener('click', () => {
    if (accept.disabled) return;
    finish(overlay, resolve, 'accepted');
  });
}

function renderDecline(overlay: HTMLElement, resolve: (decision: CguModalDecision) => void): void {
  overlay.innerHTML = `
    <div class="cgu-block">
      <div class="cgu-block-icon" aria-hidden="true">🔒</div>
      <h2>Accès refusé</h2>
      <p>L'acceptation des Conditions Générales d'Utilisation est obligatoire pour accéder à Nexxus ERP.</p>
      <button type="button" id="cgu-back">← Revenir aux CGU</button>
    </div>
  `;
  overlay.querySelector('#cgu-back')?.addEventListener('click', () => {
    renderModal(overlay, resolve);
  });
}

function finish(
  overlay: HTMLElement,
  resolve: (decision: CguModalDecision) => void,
  decision: CguModalDecision,
): void {
  overlay.dispatchEvent(new Event('cgu:done'));
  overlay.remove();
  resolve(decision);
}
